'use client';

import { c as _c } from "react/compiler-runtime";
import type { GroupProps, SlotRecipeProps } from '@chakra-ui/react';
import { Avatar as ChakraAvatar, Group } from '@chakra-ui/react';
import { forwardRef } from 'react';
type ImageProps = React.ImgHTMLAttributes<HTMLImageElement>;
export interface AvatarProps extends ChakraAvatar.RootProps {
  name?: string;
  src?: string;
  srcSet?: string;
  loading?: ImageProps['loading'];
  icon?: React.ReactElement;
  fallback?: React.ReactNode;
}
export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(function Avatar(props, ref) {
  const $ = _c(23);
  let children;
  let fallback;
  let icon;
  let loading;
  let name;
  let rest;
  let src;
  let srcSet;
  if ($[0] !== props) {
    ({
      name,
      src,
      srcSet,
      loading,
      icon,
      fallback,
      children,
      ...rest
    } = props);
    $[0] = props;
    $[1] = children;
    $[2] = fallback;
    $[3] = icon;
    $[4] = loading;
    $[5] = name;
    $[6] = rest;
    $[7] = src;
    $[8] = srcSet;
  } else {
    children = $[1];
    fallback = $[2];
    icon = $[3];
    loading = $[4];
    name = $[5];
    rest = $[6];
    src = $[7];
    srcSet = $[8];
  }
  let t0;
  if ($[9] !== fallback || $[10] !== icon || $[11] !== name) {
    t0 = <AvatarFallback name={name} icon={icon}>{fallback}</AvatarFallback>;
    $[9] = fallback;
    $[10] = icon;
    $[11] = name;
    $[12] = t0;
  } else {
    t0 = $[12];
  }
  let t1;
  if ($[13] !== loading || $[14] !== src || $[15] !== srcSet) {
    t1 = <ChakraAvatar.Image src={src} srcSet={srcSet} loading={loading} />;
    $[13] = loading;
    $[14] = src;
    $[15] = srcSet;
    $[16] = t1;
  } else {
    t1 = $[16];
  }
  let t2;
  if ($[17] !== children || $[18] !== ref || $[19] !== rest || $[20] !== t0 || $[21] !== t1) {
    t2 = <ChakraAvatar.Root ref={ref} {...rest}>{t0}{t1}{children}</ChakraAvatar.Root>;
    $[17] = children;
    $[18] = ref;
    $[19] = rest;
    $[20] = t0;
    $[21] = t1;
    $[22] = t2;
  } else {
    t2 = $[22];
  }
  return t2;
});
interface AvatarFallbackProps extends ChakraAvatar.FallbackProps {
  name?: string;
  icon?: React.ReactElement;
}
const AvatarFallback = forwardRef<HTMLDivElement, AvatarFallbackProps>(function AvatarFallback(props, ref) {
  const $ = _c(18);
  let children;
  let icon;
  let name;
  let rest;
  if ($[0] !== props) {
    ({
      name,
      icon,
      children,
      ...rest
    } = props);
    $[0] = props;
    $[1] = children;
    $[2] = icon;
    $[3] = name;
    $[4] = rest;
  } else {
    children = $[1];
    icon = $[2];
    name = $[3];
    rest = $[4];
  }
  let t0;
  if ($[5] !== children || $[6] !== name) {
    t0 = name != null && children == null && <>{getInitials(name)}</>;
    $[5] = children;
    $[6] = name;
    $[7] = t0;
  } else {
    t0 = $[7];
  }
  let t1;
  if ($[8] !== children || $[9] !== icon || $[10] !== name) {
    t1 = name == null && children == null && <ChakraAvatar.Icon asChild={!!icon}>{icon}</ChakraAvatar.Icon>;
    $[8] = children;
    $[9] = icon;
    $[10] = name;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  let t2;
  if ($[12] !== children || $[13] !== ref || $[14] !== rest || $[15] !== t0 || $[16] !== t1) {
    t2 = <ChakraAvatar.Fallback ref={ref} {...rest}>{children}{t0}{t1}</ChakraAvatar.Fallback>;
    $[12] = children;
    $[13] = ref;
    $[14] = rest;
    $[15] = t0;
    $[16] = t1;
    $[17] = t2;
  } else {
    t2 = $[17];
  }
  return t2;
});
function getInitials(name: string) {
  const names = name.trim().split(' ');
  const firstName = names[0] != null ? names[0] : '';
  const lastName = names.length > 1 ? names[names.length - 1] : '';
  return firstName && lastName ? `${firstName.charAt(0)}${lastName.charAt(0)}` : firstName.charAt(0);
}
interface AvatarGroupProps extends GroupProps, SlotRecipeProps<'avatar'> {}
export const AvatarGroup = forwardRef<HTMLDivElement, AvatarGroupProps>(function AvatarGroup(props, ref) {
  const $ = _c(15);
  let borderless;
  let rest;
  let size;
  let variant;
  if ($[0] !== props) {
    ({
      size,
      variant,
      borderless,
      ...rest
    } = props);
    $[0] = props;
    $[1] = borderless;
    $[2] = rest;
    $[3] = size;
    $[4] = variant;
  } else {
    borderless = $[1];
    rest = $[2];
    size = $[3];
    variant = $[4];
  }
  let t0;
  if ($[5] !== borderless || $[6] !== size || $[7] !== variant) {
    t0 = {
      size,
      variant,
      borderless
    };
    $[5] = borderless;
    $[6] = size;
    $[7] = variant;
    $[8] = t0;
  } else {
    t0 = $[8];
  }
  let t1;
  if ($[9] !== ref || $[10] !== rest) {
    t1 = <Group gap="0" spaceX="-3" ref={ref} {...rest} />;
    $[9] = ref;
    $[10] = rest;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  let t2;
  if ($[12] !== t0 || $[13] !== t1) {
    t2 = <ChakraAvatar.PropsProvider value={t0}>{t1}</ChakraAvatar.PropsProvider>;
    $[12] = t0;
    $[13] = t1;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
});